<script setup lang="ts">
import type { AccountType } from '~/types/user'

interface UserInfo {
  id: number
  firstname: string
  lastname: string
  account_type: AccountType
  followers_count: number
  profile_photo_thumb_url?: string
  profile_photo_large_url?: string
  is_followed_by_me?: boolean
  has_follow_request_by_me?: boolean
}

const props = defineProps<{
  user?: UserInfo
  creationDate?: Date
  preferDateInfo?: boolean
}>()

const mainStore = useMainStore()
const authStore = useAuthStore()

const { t } = useI18n()

const formattedTimeDifference = computed(() => {
  if (!props.creationDate) {
    return
  }

  return formatTimeDifference(props.creationDate)
})

const formattedCreationDate = computed(() => {
  if (!props.creationDate) {
    return
  }

  const formatter = new Intl.DateTimeFormat('default', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  return formatter.format(props.creationDate)
})

const isOwner = computed(() => {
  return authStore.currentUser?.id === props.user?.id
})
</script>

<template>
  <div class="tw-flex tw-items-center tw-gap-3">
    <NuxtLink
      class="tw-relative"
      :to="props.user ? '/user/' + props.user.id : ''"
      :external="mainStore.isLoadedFromApp"
    >
      <RelationshipUserImage
        :user="user"
        thumb
        class="tw-h-10 tw-w-10 tw-rounded-full tw-object-cover"
      />

      <UserTypeItem
        v-if="user"
        class="tw-absolute -tw-right-1 -tw-top-1 tw-h-5 tw-w-5"
        :account-type="user.account_type"
      />
    </NuxtLink>

    <div class="tw-leading-none">
      <NuxtLink
        :to="props.user ? '/user/' + props.user.id : ''"
        :external="mainStore.isLoadedFromApp"
        class="tw-mb-0.5 tw-inline-block tw-font-medium tw-leading-none"
      >
        {{ props.user?.firstname ?? '-' }} {{ props.user?.lastname ?? '-' }}
      </NuxtLink>

      <div
        v-if="
          !isOwner &&
          !props.user?.is_followed_by_me &&
          !props.user?.has_follow_request_by_me &&
          !props.preferDateInfo
        "
        class="tw-text-sm tw-leading-none"
      >
        <p>
          <span class="tw-font-bold">
            {{ formatUserNumber(props.user?.followers_count ?? 0) }}&nbsp;
          </span>

          <span class="tw-opacity-60">{{ t('Followers') }}</span>
        </p>
      </div>

      <div v-else-if="props.creationDate" class="tw-text-sm tw-leading-none tw-opacity-60">
        <span :title="formattedCreationDate">{{ formattedTimeDifference }}</span>
      </div>
    </div>

    <slot />
  </div>
</template>
